import {
	Alert,
	Button,
	Input,
	Modal,
	message,
	Popconfirm,
	Space,
	Table,
	Typography,
} from "antd";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { formatApiErrorDetail } from "../utils/apiErrors";
import useAxios from "../utils/useAxios";

// Issue #154: 部門マスタの管理画面（一覧・新規作成・改名・削除）。
// 稼働日数マスタ（WorkingDaysMasterModal）と同型のモーダルで、ヘッダーの
// Admin page メニューと Dashboard の FloatButton から開く。
//
// 正規化・重複判定・削除可否はすべてバックエンド（Issue #152）が持ち、この画面は
// 結果を表示するだけにする。フロント側で BOM 除去・NFKC・casefold 等の正規化や
// 重複のグルーピングを再実装しない（判定ロジックの所有をバックエンドに一本化する）。
//
// 一覧は既定のクエリ（for_selection なし）で取得する。プルダウン用途の
// for_selection=1 は空欄名・表記ゆれ重複を除いて返すため、この画面が検出対象に
// している異常データがまさに落ちてしまう。

const ERROR_LOAD = "Failed to load departments. Please try again.";
const ERROR_ADD = "Failed to add the department. Please try again.";
const ERROR_RENAME = "Failed to rename the department. Please try again.";
const ERROR_DELETE = "Failed to delete the department. Please try again.";
const ERROR_NAME_REQUIRED = "Department name is required.";

// 400 応答の name 項目だけを取り出してインライン表示に使う。formatApiErrorDetail は
// 「name: 〜」と項目名を前置するが、入力欄の直下に出す文言としては冗長なため。
const describeNameError = (error, fallbackMessage) => {
	const payload = error?.response?.data;
	const nameErrors = payload?.name;
	if (Array.isArray(nameErrors) && nameErrors.length > 0) {
		return nameErrors.join(" ");
	}
	if (typeof nameErrors === "string" && nameErrors) {
		return nameErrors;
	}
	return formatApiErrorDetail(payload, fallbackMessage);
};

// 一覧の Members 列は在籍者数だけを数える。退職者は Edit User の一覧に出ないため
// 付け替えができず、在籍 0 人に見える部門でもバックエンドが 409 で拒否することが
// ある。そのときは内訳を添えて、なぜ削除できないのかを伝える。
const describeDeleteError = (error) => {
	if (error?.response?.status !== 409) {
		return formatApiErrorDetail(error?.response?.data, ERROR_DELETE);
	}
	const data = error.response.data || {};
	const total = data.total_member_count ?? 0;
	const retired = data.retired_member_count ?? 0;
	if (retired > 0) {
		// 退職者は Edit User の一覧に出ない（get_users は isDeleted=False で絞る）ため、
		// この画面からは付け替えられない。できない操作を案内しない。
		return `${total} members (including ${retired} retired) still belong to this department. Retired members cannot be reassigned from Edit User, so ask your system administrator to move them.`;
	}
	return `${total} members still belong to this department. Reassign them first via Edit User.`;
};

// 保存された名前が入力と違う場合（前後空白の除去・全角から半角への統一等）は、
// 何という名前で登録されたのかを利用者へ知らせる。
const describeSavedName = (requestedName, savedName, verb) => {
	if (savedName && savedName !== requestedName) {
		return `Department ${verb} as "${savedName}".`;
	}
	return `Department ${verb}.`;
};

const DepartmentManagementModal = ({ open, onClose }) => {
	const api = useAxios();
	const [departments, setDepartments] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadFailed, setLoadFailed] = useState(false);
	const [searchText, setSearchText] = useState("");
	// 取得リクエストの発行順。最後発の応答だけを画面へ反映するために使う。
	const requestIdRef = useRef(0);

	const [newName, setNewName] = useState("");
	const [addError, setAddError] = useState(null);
	const [adding, setAdding] = useState(false);

	// 改名は行内インライン編集（Edit で名前セルが入力欄になり Save / Cancel）。
	const [editingId, setEditingId] = useState(null);
	const [editingName, setEditingName] = useState("");
	const [editError, setEditError] = useState(null);
	const [savingId, setSavingId] = useState(null);

	const fetchDepartments = useCallback(async () => {
		// 取得は開き直し・追加・改名・削除の後と複数の経路から走るため、応答が
		// 発行順に返るとは限らない。先発の応答（特に失敗）が後発の結果を
		// 上書きしないよう、最後に発行したものだけを反映する
		// （WorkingDaysMasterModal が PR #100 で入れたのと同じ対処）。
		const requestId = ++requestIdRef.current;
		const isLatest = () => requestIdRef.current === requestId;
		setLoading(true);
		try {
			const response = await api.get("/department/");
			if (!isLatest()) return;
			setDepartments(Array.isArray(response.data) ? response.data : []);
			setLoadFailed(false);
		} catch (error) {
			if (!isLatest()) return;
			// 失敗したまま前回の一覧を残すと、別の管理者が改名・削除した後の古い行に
			// 対して Edit / Delete を実行できてしまう。空にして操作ごと止める。
			setDepartments([]);
			setLoadFailed(true);
			message.error(formatApiErrorDetail(error.response?.data, ERROR_LOAD));
		} finally {
			if (isLatest()) setLoading(false);
		}
	}, [api]);

	useEffect(() => {
		if (!open) return;
		// 開くたびに前回の検索語・入力中の値・編集状態を持ち越さない
		setSearchText("");
		setNewName("");
		setAddError(null);
		setEditingId(null);
		setEditingName("");
		setEditError(null);
		fetchDepartments();
		// open の変化時のみ再取得する（api は毎レンダー新インスタンスのため依存に含めない）
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// 空欄名・正規化後に重複する名前は、バックエンドが行ごとに真偽値で返す。
	// 本番データ整理の完了後に再発したときだけ気づけるよう、平常時は何も出さない。
	const hasAnomaly = useMemo(
		() =>
			departments.some(
				(department) => department.is_blank_name || department.is_duplicate,
			),
		[departments],
	);

	// 部門は 20 件規模のためサーバー側検索は用意せず、取得済みの一覧を絞り込む
	const visibleDepartments = useMemo(() => {
		const keyword = searchText.trim().toLowerCase();
		if (!keyword) return departments;
		return departments.filter((department) =>
			(department.name || "").toLowerCase().includes(keyword),
		);
	}, [departments, searchText]);

	const handleAdd = async () => {
		const requestedName = newName;
		if (!requestedName.trim()) {
			setAddError(ERROR_NAME_REQUIRED);
			return;
		}
		setAdding(true);
		setAddError(null);
		try {
			const response = await api.post("/department/", { name: requestedName });
			message.success(
				describeSavedName(requestedName, response.data?.name, "added"),
			);
			setNewName("");
			await fetchDepartments();
		} catch (error) {
			setAddError(describeNameError(error, ERROR_ADD));
		} finally {
			setAdding(false);
		}
	};

	const startEdit = (record) => {
		setEditingId(record.id);
		setEditingName(record.name || "");
		setEditError(null);
	};

	const cancelEdit = () => {
		setEditingId(null);
		setEditingName("");
		setEditError(null);
	};

	const handleRename = async (record) => {
		const requestedName = editingName;
		if (!requestedName.trim()) {
			setEditError(ERROR_NAME_REQUIRED);
			return;
		}
		setSavingId(record.id);
		setEditError(null);
		try {
			const response = await api.patch(`/department/${record.id}/`, {
				name: requestedName,
			});
			message.success(
				describeSavedName(requestedName, response.data?.name, "renamed"),
			);
			cancelEdit();
			await fetchDepartments();
		} catch (error) {
			setEditError(describeNameError(error, ERROR_RENAME));
		} finally {
			setSavingId(null);
		}
	};

	const handleDelete = (record) => {
		const memberCount = record.member_count ?? 0;
		if (memberCount > 0) {
			// バックエンドも 409 で拒否する（二重防御）。ここで止めるのは、押しても
			// 必ず失敗する操作を投げさせないため。
			Modal.info({
				title: "This department is still in use",
				content: `${memberCount} members still belong to this department. Reassign them first via Edit User.`,
			});
			return;
		}
		Modal.confirm({
			title: `Delete "${record.name}"?`,
			content: "No members belong to it.",
			okText: "Delete",
			okButtonProps: { danger: true },
			onOk: async () => {
				try {
					await api.delete(`/department/${record.id}/`);
					message.success("Department deleted.");
				} catch (error) {
					message.error(describeDeleteError(error));
				}
				// 成功・失敗どちらでも最新の一覧へ揃える（別の管理者の操作も拾う）
				await fetchDepartments();
			},
		});
	};

	const renderSaveButton = (record) => {
		const memberCount = record.member_count ?? 0;
		const saveButton = (
			<Button
				type="link"
				size="small"
				loading={savingId === record.id}
				onClick={memberCount > 0 ? undefined : () => handleRename(record)}
			>
				Save
			</Button>
		);
		if (memberCount === 0) {
			return saveButton;
		}
		// 所属者がいる部門の改名は影響範囲を知らせてから確定する。Popconfirm の
		// 子へ onClick を残すと確認と実行の二重発火になるため、有効時は onClick を
		// 渡さず onConfirm 側だけで実行する。
		return (
			<Popconfirm
				title={`${memberCount} members will be affected. Past exported files remain unchanged.`}
				onConfirm={() => handleRename(record)}
				okText="Save"
				cancelText="Cancel"
			>
				{saveButton}
			</Popconfirm>
		);
	};

	const columns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
			defaultSortOrder: "ascend",
			render: (value, record) => {
				if (editingId !== record.id) {
					return value;
				}
				return (
					<div>
						<Input
							value={editingName}
							onChange={(event) => setEditingName(event.target.value)}
							aria-label="Department name"
							status={editError ? "error" : ""}
						/>
						{editError ? (
							<Typography.Text type="danger">{editError}</Typography.Text>
						) : null}
					</div>
				);
			},
		},
		{
			title: "Members",
			dataIndex: "member_count",
			key: "member_count",
			width: 110,
			sorter: (a, b) => (a.member_count ?? 0) - (b.member_count ?? 0),
		},
		{
			title: "",
			key: "actions",
			width: 170,
			render: (_, record) => {
				if (editingId === record.id) {
					return (
						<Space size={0}>
							{renderSaveButton(record)}
							<Button type="link" size="small" onClick={cancelEdit}>
								Cancel
							</Button>
						</Space>
					);
				}
				return (
					<Space size={0}>
						<Button
							type="link"
							size="small"
							disabled={editingId !== null}
							onClick={() => startEdit(record)}
						>
							Edit
						</Button>
						<Button
							type="link"
							size="small"
							danger
							disabled={editingId !== null}
							onClick={() => handleDelete(record)}
						>
							Delete
						</Button>
					</Space>
				);
			},
		},
	];

	return (
		<Modal
			title="Department Management"
			open={open}
			onCancel={onClose}
			footer={
				<Button onClick={onClose} type="primary">
					Close
				</Button>
			}
			width="95%"
			style={{ maxWidth: 800 }}
		>
			{loadFailed ? (
				<Alert
					type="error"
					showIcon
					message="Failed to load departments"
					description="The list was cleared so that no one edits or deletes an outdated entry. Reload to try again."
					action={
						<Button size="small" onClick={fetchDepartments}>
							Reload
						</Button>
					}
					style={{ marginBottom: 12 }}
				/>
			) : null}
			{hasAnomaly ? (
				<Alert
					type="warning"
					showIcon
					message="Some departments need attention"
					description="A department with a blank name or duplicated names was found. Rename or delete them to keep the list consistent."
					style={{ marginBottom: 12 }}
				/>
			) : null}
			<Space.Compact style={{ width: "100%", marginBottom: 4 }}>
				<Input
					placeholder="New department name"
					aria-label="New department name"
					value={newName}
					onChange={(event) => setNewName(event.target.value)}
					onPressEnter={handleAdd}
					status={addError ? "error" : ""}
				/>
				<Button type="primary" loading={adding} onClick={handleAdd}>
					Add
				</Button>
			</Space.Compact>
			{addError ? (
				<div style={{ marginBottom: 8 }}>
					<Typography.Text type="danger">{addError}</Typography.Text>
				</div>
			) : null}
			<Input
				placeholder="Search departments"
				aria-label="Search departments"
				allowClear
				value={searchText}
				onChange={(event) => setSearchText(event.target.value)}
				style={{ marginTop: 8, marginBottom: 12 }}
			/>
			<Table
				columns={columns}
				dataSource={visibleDepartments}
				rowKey="id"
				loading={loading}
				pagination={false}
				tableLayout="fixed"
				size="small"
				// 列合計は 280px + Name 列の可変分。モーダルが狭い画面で縮んだときだけ
				// テーブル内部がスクロールする（ページ全体が横スクロールしない）。
				scroll={{ x: 560 }}
			/>
		</Modal>
	);
};

export default DepartmentManagementModal;
